import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Image, message, Switch, Modal, Form, Input, Upload, Popconfirm, App } from 'antd';
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCategories } from '../../hooks/useCategories';
import noImage from '../../assets/images/no-image.png';

const Categories = () => {
    const navigate = useNavigate();
    // const { message } = App.useApp();
    const { categories, loading, error, fetchCategories, createCategory, updateCategory, deleteCategory, toggleCategoryStatus, clearError } = useCategories();


    // Fetch services on component mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Display error messages
    useEffect(() => {
        if (error) {
            message.error(typeof error === 'string' ? error : 'An error occurred');
            clearError();
        }
    }, [error, message, clearError]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const handleToggle = async (id, checked) => {
        try {
            await toggleCategoryStatus(id, checked);
            await fetchCategories(); // Refetch to ensure UI is updated
            message.success(`Service ${checked ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
            message.error('Failed to update service status');
        }
    };

    const handleAddCategory = () => {
        setEditingCategory(null);
        setIsSubmitting(false);
        setIsModalVisible(true);
        form.resetFields();
    };

    const handleEditService = (record) => {
        setEditingCategory(record);
        setIsSubmitting(false);
        setIsModalVisible(true);
        form.setFieldsValue({
            name: record.name,
            status: record.isActive,
            image: record.image ? [{
                uid: '-1',
                name: 'no-image.png',
                status: 'done',
                url: record.image,
            }] : [],
        });
    };

    const handleDeleteService = async (id) => {
        try {
            await deleteCategory(id);
            await fetchCategories(); // Refetch to ensure UI is updated
            message.success('Service deleted successfully');
        } catch (error) {
            message.error('Failed to delete service');
        }
    };

    const handleModalCancel = () => {
        if (isSubmitting) return;
        setIsModalVisible(false);
        setEditingCategory(null);
        setIsSubmitting(false);
        form.resetFields();
    };

    const handleModalOk = async () => {
        if (isSubmitting) return;

        try {
            const values = await form.validateFields();
            let imageFile = null;
            let imageUrl = editingCategory?.image || null;

            // Handle uploaded file
            if (values.image && values.image.length > 0) {
                const fileItem = values.image[0];
                if (fileItem.originFileObj) {
                    imageFile = fileItem.originFileObj;
                } else if (fileItem.url) {
                    imageUrl = fileItem.url;
                }
            }

            if (editingCategory) {
                // Update existing service
                const updateData = {
                    name: values.name,
                    isActive: values.status !== undefined ? values.status : editingCategory.isActive,
                };

                if (imageFile) {
                    updateData.image = imageFile;
                } else if (imageUrl && imageUrl !== noImage) {
                    updateData.image = imageUrl;
                }

                setIsSubmitting(true);
                try {
                    await updateCategory(editingCategory.id, updateData);
                    await fetchCategories();
                    message.success('Service updated successfully');
                    setIsModalVisible(false);
                    setEditingCategory(null);
                    form.resetFields();
                } catch (apiError) {
                    const errorMessage = typeof apiError === 'string'
                        ? apiError
                        : apiError?.response?.data?.message || apiError?.message || 'Failed to update service';
                    message.error(errorMessage);
                } finally {
                    setIsSubmitting(false);
                }
                return;
            } else {
                if (!values.name || typeof values.name !== 'string' || values.name.trim() === '') {
                    message.error('Please enter a service name');
                    return;
                }

                // Image is required
                if (!imageFile && (!imageUrl || imageUrl === noImage)) {
                    message.error('Please upload an image for the service');
                    return;
                }

                const newServiceData = {
                    name: values.name.trim(),
                    isActive: values.status !== undefined ? values.status : true,
                };

                if (imageFile) {
                    newServiceData.image = imageFile;
                } else if (imageUrl && imageUrl !== noImage) {
                    newServiceData.image = imageUrl;
                }


                setIsSubmitting(true);
                try {
                    await createCategory(newServiceData);
                    await fetchCategories();
                    message.success('Service added successfully');
                    setIsModalVisible(false);
                    setEditingCategory(null);
                    form.resetFields();
                } catch (apiError) {
                    const errorMessage = typeof apiError === 'string'
                        ? apiError
                        : apiError?.response?.data?.message || apiError?.message || 'Failed to add category';
                    message.error(errorMessage);
                } finally {
                    setIsSubmitting(false);
                }
                return;
            }
        } catch (error) {
            console.error('Validation failed:', error);
            if (error.errorFields) {
                message.error('Please fill all required fields correctly');
            } else {
                message.error('Failed to save service');
            }
        }
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error('Image must be smaller than 2MB!');
        }
        return isImage && isLt2M ? false : Upload.LIST_IGNORE;
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            width: 120,
            render: (image, record) => {

                const imageSrc = image && typeof image === 'string' && image.trim() !== '' ? image : noImage;
                return (
                    <Image
                        src={imageSrc}
                        alt="Service"
                        width={80}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        fallback={noImage}
                        preview={false}
                        onError={(e) => {
                            console.error('Image load error for:', imageSrc, e);
                            e.target.src = noImage;
                        }}
                    />
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (isActive, record) => (
                <span onClick={(e) => e.stopPropagation()}>
                    <Switch
                        checked={isActive}
                        onChange={(checked) => handleToggle(record.id, checked)}
                        checkedChildren="Active"
                        unCheckedChildren="Inactive"
                    />
                </span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEditService(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this service?"
                        onConfirm={() => handleDeleteService(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Categories</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCategory}
                >
                    Add More Category
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={categories}
                rowKey="id"
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} categories`,
                }}
                onRow={(record) => ({
                    onClick: () => navigate(`/categories/${record.id}/sub-categories`, {
                        state : {
                            name : record?.name
                        }
                    }),
                    style: { cursor: 'pointer' },
                })}
            />

            <Modal
                title={editingCategory ? "Edit Category" : "Add New Category"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={editingCategory ? "Update Category" : "Add Category"}
                cancelText="Cancel"
                width={500}
                confirmLoading={isSubmitting}
                okButtonProps={{ disabled: isSubmitting }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        status: true,
                    }}
                >
                    <Form.Item
                        label="Service Name"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter service name!',
                            },
                        ]}
                    >
                        <Input placeholder="Enter service name" />
                    </Form.Item>

                    <Form.Item
                        label="Category Image"
                        name="image"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        rules={[
                            {
                                required: !editingCategory,
                                message: 'Please upload an image!',
                            },
                        ]}
                    >
                        <Upload
                            name="image"
                            listType="picture-card"
                            beforeUpload={beforeUpload}
                            maxCount={1}
                            accept="image/*"
                        >
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        </Upload>
                    </Form.Item>

                    <Form.Item
                        label="Status"
                        name="status"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Categories;

